import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { getReactCheckoutStatus } from "../../utils/paymentsApi";
import { useAuth } from "../../contexts/AuthContext";
import "./PricingPage.css";

const FINAL_STATUSES = new Set(["success", "failed", "revoked"]);
const POLL_INTERVAL_MS = 4000;

const normalizeStatus = (payload) => {
  const raw =
    payload?.status ||
    payload?.checkout_status ||
    payload?.payment_status ||
    payload?.purchase_attempt?.status ||
    "";
  return String(raw).toLowerCase();
};

const CheckoutStatusPage = ({ mode = "success" }) => {
  const { refreshEntitlements } = useAuth();
  const [searchParams] = useSearchParams();
  const queryReference =
    searchParams.get("purchase_reference") || searchParams.get("purchaseReference");
  const storedReference =
    sessionStorage.getItem("lastCheckoutPurchaseReference") ||
    localStorage.getItem("lastCheckoutPurchaseReference");

  const purchaseReference = queryReference || storedReference || "";

  const [statusPayload, setStatusPayload] = useState(null);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(Boolean(purchaseReference));
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  const heading = useMemo(() => {
    return mode === "cancel"
      ? "Плаќањето е откажано"
      : "Проверка на плаќањето";
  }, [mode]);

  useEffect(() => {
    if (!purchaseReference) {
      setLoading(false);
      return undefined;
    }

    let active = true;

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const poll = async () => {
      try {
        const payload = await getReactCheckoutStatus(purchaseReference);
        if (!active) return;

        const nextStatus = normalizeStatus(payload) || "pending";
        setStatusPayload(payload);
        setStatus(nextStatus);
        setError("");
        setLoading(false);

        if (FINAL_STATUSES.has(nextStatus)) {
          if (nextStatus === "success") {
            refreshEntitlements();
          }
          stopPolling();
        }
      } catch (err) {
        if (!active) return;
        setLoading(false);
        setError(err.message || "Неуспешно вчитување на статус.");
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      active = false;
      stopPolling();
    };
  }, [purchaseReference, refreshEntitlements]);

  return (
    <div className="pricing-page">
      <section className="pricing-hero">
        <div className="pricing-hero-content">
          <Typography variant="overline" className="pricing-eyebrow">
            CHECKOUT STATUS
          </Typography>
          <Typography variant="h3" component="h1" className="pricing-title">
            {heading}
          </Typography>
          <Typography variant="body1" className="pricing-subtitle">
            Го проверуваме статусот на вашата нарачка. Додека webhook обработката
            не заврши, статусот може да биде `pending`.
          </Typography>
        </div>
      </section>

      <section className="pricing-grid">
        <div className="pricing-state-card">
          {!purchaseReference ? (
            <Alert severity="warning" sx={{ width: "100%" }}>
              Недостасува purchase reference. Отворете checkout повторно од
              ценовникот.
            </Alert>
          ) : null}

          {purchaseReference ? (
            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="body2">
                Purchase reference: <strong>{purchaseReference}</strong>
              </Typography>

              {loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2">Проверка во тек...</Typography>
                </Stack>
              ) : null}

              {!loading && error ? <Alert severity="error">{error}</Alert> : null}

              {!loading && !error ? (
                <Alert
                  severity={status === "success" ? "success" : status === "pending" ? "info" : "warning"}
                >
                  Тековен статус: <strong>{status}</strong>
                </Alert>
              ) : null}

              {!loading && !error && statusPayload ? (
                <Typography variant="caption" color="text.secondary">
                  Статусот автоматски се освежува на секои {POLL_INTERVAL_MS / 1000} секунди
                  додека не стане финален.
                </Typography>
              ) : null}

              <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                <Button component={Link} to="/pricing" variant="contained" className="pricing-button">
                  Назад на ценовник
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default CheckoutStatusPage;
